import { Link, useParams, useLocation, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Sparkles, Check } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import { getToolLanding, TOOL_LANDINGS } from "@/data/toolLandings";
import { getArticle } from "@/data/articles";

/**
 * /tools/:style — commercial-intent SEO pillar pages, one per Indian
 * jewellery style (Kundan, Jadau, Polki, Diamond, Temple).
 *
 * Each URL is:
 *   - Unique <title> + description + canonical (via SEOHead / helmet)
 *   - Long-form on-page content (workflow, prompts, FAQs) so the page
 *     is not a doorway
 *   - JSON-LD SoftwareApplication + FAQPage + BreadcrumbList for rich
 *     results eligibility
 *   - Internal-linked to related articles + sister tool pages to
 *     concentrate PageRank within the style cluster
 */
export default function ToolLanding() {
  // Slug source depends on which route matched:
  //  - /tools/:style  → useParams().style
  //  - /:slug         → derive from pathname (no :style param)
  // Both branches route to the same manifest entry; SEOHead below
  // then emits a single canonical pointing at the root-level URL.
  const { style } = useParams();
  const { pathname } = useLocation();
  const slug = style || pathname.replace(/^\/+/, "").split("/")[0];
  const meta = getToolLanding(slug);

  if (!meta) return <Navigate to="/for-jewellers" replace />;

  // Phase 2 URL restructure: canonical is the root-level slug.
  // Both /tools/:style and /:style render this same component, but
  // Google is told the root URL is the one authoritative version.
  const canonicalUrl = `https://facetra.studio/${meta.slug}`;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: `Facetra — ${meta.h1}`,
      operatingSystem: "Web",
      applicationCategory: "DesignApplication",
      description: meta.metaDesc,
      url: canonicalUrl,
      offers: {
        "@type": "Offer",
        priceCurrency: "INR",
        price: "3999",
        availability: "https://schema.org/InStock",
      },
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.8",
        reviewCount: "42",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: meta.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Facetra", item: "https://facetra.studio/" },
        { "@type": "ListItem", position: 2, name: "For Jewellers", item: "https://facetra.studio/for-jewellers" },
        { "@type": "ListItem", position: 3, name: meta.h1, item: canonicalUrl },
      ],
    },
  ];

  return (
    <div
      className="min-h-screen bg-[#0B0B0B] text-white relative overflow-hidden"
      data-testid={`tool-landing-${meta.slug}`}
    >
      <SEOHead
        title={meta.title}
        description={meta.metaDesc}
        canonical={canonicalUrl}
        keywords={meta.keywords}
        jsonLd={jsonLd}
      />

      <div className="absolute inset-0 radial-gold-glow opacity-30 pointer-events-none" />
      <div className="absolute inset-0 noise-overlay pointer-events-none" />

      {/* Top bar */}
      <header className="relative border-b border-white/5 bg-[#0A0A0A]/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="tool-home-link"
            className="flex items-center gap-2.5"
          >
            <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#F3CE5A] to-[#8B6914] flex items-center justify-center shadow-[0_0_18px_rgba(212,175,55,0.35)]">
              <span className="font-display text-black text-sm font-bold">F</span>
            </div>
            <span className="font-display text-lg tracking-tight">Facetra</span>
          </Link>
          <Link
            to="/for-jewellers"
            data-testid="tool-back-link"
            className="text-xs text-white/55 hover:text-[#D4AF37] inline-flex items-center gap-1.5 transition"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> For Jewellers
          </Link>
        </div>
      </header>

      <main className="relative max-w-3xl mx-auto px-6 lg:px-10 py-14 prose-legal">
        {/* Kicker + H1 */}
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-4 not-prose">
          {meta.kicker}
        </div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-white not-prose">
          {meta.h1}
        </h1>
        <p className="mt-5 text-white/70 text-base leading-relaxed not-prose">
          {meta.heroTagline}
        </p>

        {/* Hero CTAs */}
        <div className="mt-8 flex flex-wrap items-center gap-3 not-prose">
          <Link
            to="/signup"
            data-testid={`tool-cta-signup-${meta.slug}`}
            className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-5 py-2.5 text-sm font-medium text-black hover:bg-[#F3CE5A] transition"
          >
            <Sparkles className="w-4 h-4" /> Start rendering — 10 free credits
          </Link>
          <Link
            to="/how-it-works"
            data-testid={`tool-cta-how-${meta.slug}`}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm text-white/80 hover:border-[#D4AF37]/40 hover:text-white transition"
          >
            How it works
          </Link>
        </div>

        {/* Intro */}
        <div className="mt-12">
          {meta.intro.split("\n\n").map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        {/* What is */}
        <h2>{meta.whatIsHeading || `What is ${meta.h1.replace(/^AI Tool for /, "").replace(/ Jewellery Design$/, "")}?`}</h2>
        <p>{meta.whatIs}</p>

        {/* Features */}
        <h2>{meta.featuresHeading || `What Facetra does for ${meta.kicker.replace(/^.*?For /i, "")}`}</h2>
        <ul>
          {meta.features.map((f, i) => (
            <li key={i}>
              <Check className="w-4 h-4 text-[#D4AF37] inline-block mr-1 -mt-0.5" />
              {f}
            </li>
          ))}
        </ul>

        {/* Workflow */}
        <h2>The Facetra workflow (six steps, one afternoon)</h2>
        <ol>
          {meta.workflow.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ol>

        {/* Sample prompts */}
        <h2>Sample prompts to copy</h2>
        <p className="not-prose text-white/60 text-sm mb-4">
          Paste any of these prompts into Facetra Studio to see the style
          in action. Adjust weights (heavy / light), stone colours and
          motifs freely.
        </p>
        <div className="not-prose space-y-3">
          {meta.prompts.map((p, i) => (
            <div
              key={i}
              data-testid={`tool-prompt-${meta.slug}-${i}`}
              className="rounded-lg border border-white/8 bg-[#0E0E0E] p-4 text-[13px] text-white/75 leading-relaxed"
            >
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mr-2">
                Prompt {i + 1}
              </span>
              {p}
            </div>
          ))}
        </div>

        {/* Pricing */}
        <h2>How much does it cost?</h2>
        <p>
          Credit-based pricing. Studio render <strong>1 credit</strong> per
          variant, multi-view sheet <strong>4 credits</strong>, CAD-ready view{" "}
          <strong>1 credit</strong>, 3D mesh <strong>15 credits</strong>{" "}
          (Standard) or <strong>25 credits</strong> (HD). Credit packs start
          at ₹1,500 (Starter, 100 credits, ₹15/cr) and go down to ₹12/credit on
          the Atelier pack (1,300 credits at ₹15,600). New signups get{" "}
          <Link to="/signup">10 free credits</Link>.
        </p>

        {/* FAQ */}
        <h2>Frequently asked questions</h2>
        {meta.faqs.map((f, i) => (
          <div key={i} data-testid={`tool-faq-${meta.slug}-${i}`}>
            <h3>{f.q}</h3>
            <p>{f.a}</p>
          </div>
        ))}

        {/* CTA */}
        <div className="mt-14 rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/10 to-transparent p-7 not-prose">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-2">
            Get started
          </div>
          <h2 className="font-display text-2xl text-white leading-tight">
            10 free credits. No card required.
          </h2>
          <p className="mt-3 text-white/60 text-sm">
            Enough to render 15 variations, or one full CAD kit — see the
            output before committing to a pack.
          </p>
          <Link
            to="/signup"
            data-testid={`tool-cta-bottom-${meta.slug}`}
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-5 py-2.5 text-sm font-medium text-black hover:bg-[#F3CE5A] transition"
          >
            Create free account <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Related tools */}
        <h2>Related design tools</h2>
        <div className="not-prose grid grid-cols-1 md:grid-cols-3 gap-4">
          {meta.relatedTools
            .map((s) => TOOL_LANDINGS[s])
            .filter(Boolean)
            .map((t) => (
              <Link
                key={t.slug}
                to={`/${t.slug}`}
                data-testid={`tool-related-tool-${t.slug}`}
                className="group rounded-xl border border-white/8 bg-[#0E0E0E] p-4 hover:border-[#D4AF37]/30 transition"
              >
                <div className="text-[9.5px] uppercase tracking-[0.22em] text-white/40 mb-2">
                  {t.kicker}
                </div>
                <div className="text-[13px] text-white font-medium leading-snug group-hover:text-[#D4AF37] transition">
                  {t.h1}
                </div>
              </Link>
            ))}
        </div>

        {/* Related articles */}
        <h2>Related reading</h2>
        <div className="not-prose grid grid-cols-1 md:grid-cols-3 gap-4">
          {meta.related
            .map((s) => getArticle(s))
            .filter(Boolean)
            .map((a) => (
              <Link
                key={a.slug}
                to={`/articles/${a.slug}`}
                data-testid={`tool-related-article-${a.slug}`}
                className="group rounded-xl border border-white/8 bg-[#0E0E0E] p-4 hover:border-[#D4AF37]/30 transition"
              >
                <div className="text-[9.5px] uppercase tracking-[0.22em] text-white/40 mb-2">
                  Article
                </div>
                <div className="text-[13px] text-white font-medium leading-snug group-hover:text-[#D4AF37] transition">
                  {a.title}
                </div>
              </Link>
            ))}
        </div>

        {/* Footer */}
        <div className="mt-14 pt-8 border-t border-white/5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-white/40 not-prose">
          <span>© {new Date().getFullYear()} Facetra Design Studio Private Limited</span>
          <div className="flex items-center gap-5">
            <Link to="/how-it-works" className="hover:text-white/70 transition">How it works</Link>
            <Link to="/glossary" className="hover:text-white/70 transition">Glossary</Link>
            <Link to="/articles" className="hover:text-white/70 transition">Articles</Link>
            <Link to="/contact" className="hover:text-white/70 transition">Contact</Link>
          </div>
        </div>
      </main>

      {/* Same prose token bundle as ArticleLayout — kept local so this
          page is self-contained even if legal styles get refactored. */}
      <style>{`
        .prose-legal h2 {
          font-family: 'Fraunces', serif;
          font-size: 1.6rem;
          letter-spacing: -0.01em;
          color: #fff;
          margin-top: 2.5rem;
          margin-bottom: 0.85rem;
        }
        .prose-legal h3 {
          font-size: 1rem;
          font-weight: 600;
          color: #D4AF37;
          margin-top: 1.5rem;
          margin-bottom: 0.5rem;
        }
        .prose-legal p, .prose-legal li {
          font-size: 0.95rem;
          color: rgba(255,255,255,0.72);
          line-height: 1.75;
        }
        .prose-legal p { margin-bottom: 0.95rem; }
        .prose-legal ul, .prose-legal ol { padding-left: 1.15rem; margin-bottom: 0.95rem; }
        .prose-legal ul li { list-style: disc; margin-bottom: 0.4rem; }
        .prose-legal ol li { list-style: decimal; margin-bottom: 0.4rem; }
        .prose-legal strong { color: #fff; font-weight: 600; }
        .prose-legal a { color: #D4AF37; text-decoration: underline; text-decoration-color: rgba(212,175,55,0.4); }
        .prose-legal a:hover { color: #F3CE5A; }
      `}</style>
    </div>
  );
}
